import type {
  AnimationAction,
  Group,
  KeyframeTrack,
} from '@anov/3d-core'
import {
  AnimationClip,
  AnimationMixer,
  Box3,
  LoopRepeat,
  NumberKeyframeTrack,
  VectorKeyframeTrack,
} from '@anov/3d-core'

// 动画类型
enum EMarkAnimationType {
  Bounce = 'bounce', // 上下弹跳
  Pulse = 'pulse', // 缩放
  Rotate = 'rotate', // 旋转
  Flash = 'flash', // 闪烁
}

interface AnimationOption {
  enable: boolean
  type: EMarkAnimationType | EMarkAnimationType[]
  duration: number
}

class KeyframeAnimation {
  private _target: Group

  private _duration: number
  private _type: EMarkAnimationType | EMarkAnimationType[]
  private _enable: boolean
  private _mixer: AnimationMixer
  private _animations: Map<EMarkAnimationType, AnimationAction> = new Map()
  private _animationClips: Map<EMarkAnimationType, AnimationClip> = new Map()

  constructor(target: Group, options: AnimationOption) {
    this._target = target
    const { type, duration = 1, enable = true } = options
    this._type = type
    this._duration = duration
    this._enable = enable
    this._mixer = new AnimationMixer(target)

    this.setupAnimations()
  }

  /**
       * 获取动画混合器
       */
  public get mixer(): AnimationMixer {
    return this._mixer
  }

  public get duration(): number {
    return this._duration
  }

  public set duration(value: number) {
    if (this._duration === value)
      return
    this._duration = value
    this.reset()
    this.setupAnimations()
  }

  public get type() {
    return this._type
  }

  public set type(value: EMarkAnimationType | EMarkAnimationType[]) {
    this._type = value
    this.reset()
    this.setupAnimations()
  }

  public get enable(): boolean {
    return this._enable
  }

  /**
       * 重置所有动画
       */
  public reset(): void {
    this._mixer.stopAllAction()

    this._animationClips.forEach((clip) => {
      this._mixer.uncacheClip(clip)
    })

    this._animations.clear()
    this._animationClips.clear()
  }

  /**
       * 设置动画
       */
  private setupAnimations(): void {
    if (!this.enable)
      return

    const types = Array.isArray(this.type)
      ? this.type
      : [this.type]

    // 获取Mesh的高度、位置
    const { height } = this.getBoxSize(this._target)
    const positionY = this._target?.position.y

    types.forEach((type) => {
      const tracks = this.createKeyframeTracks(type, this._duration, { position: positionY, height: height / 2 })

      const animationClip = new AnimationClip(
        `${type}`,
        this.duration,
        tracks,
      )

      this._animationClips.set(type, animationClip)
      const action = this._mixer.clipAction(animationClip)

      // 配置动画Action
      action
        .setLoop(LoopRepeat, Infinity)
        .clampWhenFinished = true

      this._animations.set(type, action)
    })
  }

  private getBoxSize(mesh) {
    // mesh.updateWorldMatrix(true, true)
    const { max, min } = new Box3().setFromObject(mesh)
    return { width: max.x - min.x, height: max.y - min.y }
  }

  /**
       * 创建关键帧轨道
       */
  private createKeyframeTracks(
    type: EMarkAnimationType,
    duration: number,
    trackParams: { height: number; position: number },
  ): KeyframeTrack[] {
    const tracks: KeyframeTrack[] = []

    switch (type) {
      case EMarkAnimationType.Bounce:
        // eslint-disable-next-line no-case-declarations
        const { position = 0, height = 0 } = trackParams
        tracks.push(
          new VectorKeyframeTrack(
            '.position',
            [0, duration / 2, duration],
            [0, position, 0, 0, position + height, 0, 0, position, 0],
          ),
        )
        break

      case EMarkAnimationType.Pulse:
        tracks.push(
          new VectorKeyframeTrack(
            '.scale',
            [0, duration / 2, duration],
            [1, 1, 1, 1.2, 1.2, 1.2, 1, 1, 1],
          ),
        )
        break

      case EMarkAnimationType.Rotate:
        tracks.push(
          new NumberKeyframeTrack(
            '.rotation[y]',
            [0, duration],
            [0, Math.PI * 2],
          ),
        )
        break

      case EMarkAnimationType.Flash:
        tracks.push(
          new NumberKeyframeTrack(
            '.material.opacity',
            [0, duration / 2, duration],
            [1, 0.2, 1],
          ),
        )
        break

      default:
        console.error(`animation type ${type} does not exist`)
        break
    }

    return tracks
  }

  /**
       * 更新动画
       */
  public update(delta: number): void {
    if (this._mixer)
      this._mixer.update(delta)
  }

  /**
       * 播放动画
       */
  public play(type?: EMarkAnimationType): void {
    if (type) {
      const animation = this._animations.get(type)
      if (animation)
        animation.reset().play()
    }
    else {
      this._animations.forEach((animation) => {
        animation.reset().play()
      })
    }
  }

  /**
       * 停止动画
       */
  public stop(type?: EMarkAnimationType): void {
    if (type) {
      const animation = this._animations.get(type)
      animation?.stop()
    }
    else {
      this._animations.forEach((animation) => {
        animation.stop()
      })
    }
  }

  /**
       * 销毁资源
       */
  public destroy(): void {
    this.reset()
    this._mixer = null
  }
}

export { KeyframeAnimation, EMarkAnimationType, AnimationOption }